import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Play, Pause, Trophy, Users, Timer, Flag, ChevronRight, ExternalLink, Download, Crown, Award, X, Trash2 } from "lucide-react";
import Confetti from "react-confetti";
import { formatScore, scoreLabel } from "@/lib/scoreFormat";

export default function TournamentDetail() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [tick, setTick] = useState(0);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [pendingRoundIdx, setPendingRoundIdx] = useState(null);
  const [scoresInput, setScoresInput] = useState({});
  const alarmPlayed = useRef(false);

  const load = async () => {
    try {
      const r = await api.get(`/tournaments/${tid}`);
      setT(r.data);
    } catch (e) {
      toast.error("Not found");
      navigate("/dashboard");
    }
  };

  useEffect(() => { load(); }, [tid]);

  // Ticker for timer
  useEffect(() => {
    const iv = setInterval(() => setTick(x => x + 1), 250);
    return () => clearInterval(iv);
  }, []);

  if (!t) return <div className="min-h-screen bg-slate-950"><Nav /><div className="p-10 text-slate-500">Loading…</div></div>;

  const isOrg = t.is_organizer;
  const inviteUrl = `${window.location.origin}/join/${t.invite_code}`;
  const scoreboardUrl = `${window.location.origin}/scoreboard/${t.tournament_id}`;

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  // Determine active round
  const activeRound = t.rounds.find(r => r.status === "active");
  const readyRound = t.rounds.find(r => r.status === "ready");
  const scorableRoundIdx = t.rounds.findIndex(r => r.status === "active" || r.status === "scored");

  // Timer calculation (accounts for pauses)
  let remainingSec = null;
  let isPaused = false;
  if (activeRound?.started_at) {
    const start = new Date(activeRound.started_at).getTime();
    const total = t.timer_minutes * 60 * 1000;
    const pausedMs = activeRound.paused_ms || 0;
    const nowRef = activeRound.paused_at ? new Date(activeRound.paused_at).getTime() : Date.now();
    isPaused = !!activeRound.paused_at;
    const remainingMs = start + total + pausedMs - nowRef;
    remainingSec = Math.max(0, Math.floor(remainingMs / 1000));
    if (remainingSec === 0 && !isPaused && !alarmPlayed.current) {
      alarmPlayed.current = true;
      playAlarm();
      toast.info("⏰ Round time's up!");
    }
  }

  const fmt = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const setupRound = async (round_number) => {
    try {
      const r = await api.post(`/tournaments/${tid}/setup-round`, { round_number });
      setT({ ...r.data, is_organizer: true, standings: r.data.standings || t.standings || [] });
      toast.success(`Round ${round_number} set — players seated!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to set up round");
      console.error("setup-round error", e.response?.status, e.response?.data);
    }
  };

  const startRound = async (round_number) => {
    try {
      alarmPlayed.current = false;
      await api.post(`/tournaments/${tid}/start-round`, { round_number });
      toast.success("Round started — timer running");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to start round");
      console.error("start-round error", e.response?.status, e.response?.data);
    }
  };

  const pauseRound = async (round_number) => {
    try {
      await api.post(`/tournaments/${tid}/pause-round`, { round_number });
      toast.info("Timer paused");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to pause");
    }
  };

  const resumeRound = async (round_number) => {
    try {
      alarmPlayed.current = false;
      await api.post(`/tournaments/${tid}/resume-round`, { round_number });
      toast.success("Timer resumed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resume");
    }
  };

  const openScoreDialog = (idx) => {
    const rnd = t.rounds[idx];
    const seated = rnd.tables.flatMap(x => x.player_ids);
    const initial = {};
    seated.forEach(uid => { initial[uid] = ""; });
    // Pre-fill existing scores when the round is already locked (edit mode)
    if (rnd.status === "locked" && Array.isArray(rnd.scores)) {
      rnd.scores.forEach(s => { if (s.user_id in initial) initial[s.user_id] = String(s.points ?? ""); });
    }
    setScoresInput(initial);
    setPendingRoundIdx(idx);
    setScoreDialogOpen(true);
  };

  const reshuffleRound = async (round_number) => {
    try {
      await api.post(`/tournaments/${tid}/reshuffle-round`, { round_number });
      toast.success("Seating reshuffled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reshuffle");
    }
  };

  const submitScores = async () => {
    if (pendingRoundIdx === null) return;
    const rnd = t.rounds[pendingRoundIdx];
    const seatedIds = new Set(rnd.tables.flatMap(x => x.player_ids));
    // For bracket, ensure every table has a clear winner
    if (t.tournament_type === "bracket") {
      for (const tbl of rnd.tables) {
        const [a, b] = tbl.player_ids;
        const sa = Number(scoresInput[a] || 0);
        const sb = Number(scoresInput[b] || 0);
        if (sa === sb) {
          toast.error(`Table ${tbl.table_number}: pick a winner before locking the match`);
          return;
        }
      }
    }
    const scores = Object.entries(scoresInput)
      .filter(([uid]) => seatedIds.has(uid))
      .map(([user_id, points]) => ({ user_id, points: Number(points) || 0 }));
    const isEdit = rnd.status === "locked";
    const path = isEdit ? "edit-round-scores" : "complete-round";
    try {
      await api.post(`/tournaments/${tid}/${path}`, { round_number: rnd.round_number, scores });
      toast.success(isEdit ? "Scores updated" : "Round scores locked!");
      setScoreDialogOpen(false);
      setPendingRoundIdx(null);
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || (isEdit ? "Failed to update scores" : "Failed to lock round");
      toast.error(typeof msg === "string" ? msg : (isEdit ? "Failed to update scores" : "Failed to lock round"));
      console.error(`${path} error`, e.response?.status, e.response?.data);
    }
  };

  const finishTournament = async () => {
    try {
      await api.post(`/tournaments/${tid}/finish`);
      toast.success("Tournament finished!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to finish");
      console.error("finish error", e.response?.status, e.response?.data);
    }
  };

  const cancelTournament = async () => {
    if (!window.confirm("Cancel the tournament? Current scores will be locked and remaining rounds will be skipped. This can't be undone.")) return;
    try {
      await api.post(`/tournaments/${tid}/cancel`);
      toast.success("Tournament cancelled — scores locked");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to cancel");
    }
  };

  const deleteTournament = async () => {
    if (!window.confirm("Permanently delete this tournament? It will vanish from history and cannot be recovered.")) return;
    try {
      await api.delete(`/tournaments/${tid}`);
      toast.success("Tournament deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to delete");
    }
  };

  const nameOf = (uid) => t.participants.find(p => p.user_id === uid)?.name || uid;

  const nextRoundIdx = t.rounds.findIndex(r => r.status === "pending" && !r.is_championship);
  const championshipIdx = t.rounds.findIndex(r => r.is_championship);
  const championshipRnd = t.rounds[championshipIdx];
  const regularRoundsDone = t.rounds.filter(r => !r.is_championship && r.status === "locked").length;
  const canStartChampionship = championshipRnd?.status === "pending" && regularRoundsDone > 0;

  const exportPdf = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tournaments/${tid}/export-pdf`, "_blank");
  };

  const isFinished = t.status === "finished" || t.status === "archived";

  return (
    <div className="min-h-screen bg-slate-950">
      {isFinished && <Confetti recycle={false} numberOfPieces={280} colors={["#f59e0b", "#10b981", "#f43f5e", "#f8fafc"]} />}
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500 font-mono-data tracking-widest">TOURNAMENT</span>
              <StatusPill status={t.status} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mt-2 text-slate-50">{t.name}</h1>
            {t.game_name && <p className="text-slate-400 mt-1 font-mono-data text-sm uppercase tracking-wider">{t.game_name}</p>}
            <div className="mt-2 flex gap-2 flex-wrap">
              <span className="text-[10px] font-mono-data tracking-widest px-2 py-1 rounded-md border uppercase border-sky-500/40 bg-sky-500/10 text-sky-300" data-testid="tournament-type-badge">
                {t.tournament_type === "bracket" ? "Single Elimination" : t.tournament_type === "placement" ? "Placement Points" : "Average Share"}
              </span>
              {t.tournament_type === "placement" && Array.isArray(t.placement_points) && t.placement_points.length > 0 && (
                <span className="text-[10px] font-mono-data tracking-widest px-2 py-1 rounded-md border border-white/10 bg-slate-900/60 text-slate-300 uppercase" data-testid="placement-points-summary">
                  {t.placement_points.map((p, i) => `${i+1}=${p}`).join(" · ")}
                </span>
              )}
              {t.tournament_type === "bracket" && (
                <span className="text-[10px] font-mono-data tracking-widest px-2 py-1 rounded-md border border-white/10 bg-slate-900/60 text-slate-300 uppercase">
                  Seeding: {t.bracket_seeding || "random"}
                </span>
              )}
            </div>
            {t.cancelled && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs font-mono-data tracking-widest uppercase">
                <X className="w-3 h-3" /> Cancelled — Ended early
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to={`/scoreboard/${t.tournament_id}`} target="_blank" data-testid="open-scoreboard">
              <Button variant="outline" className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10"><ExternalLink className="w-4 h-4 mr-2" /> Live Scoreboard</Button>
            </Link>
            {isFinished && (
              <>
                <Button onClick={exportPdf} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="export-pdf"><Download className="w-4 h-4 mr-2" /> Export PDF</Button>
                {isOrg && (
                  <Button onClick={deleteTournament} variant="outline" className="border-rose-500/40 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10" data-testid="delete-tournament">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Invite + stats grid */}
        <div className="mt-10 grid lg:grid-cols-4 gap-4">
          <Card className="lg:col-span-2">
            <Section label="Invite link" />
            <div className="mt-3 flex gap-2">
              <Input readOnly value={inviteUrl} className="bg-slate-950 border-white/10 font-mono-data text-xs" data-testid="invite-url" />
              <Button onClick={() => copy(inviteUrl)} variant="outline" className="border-white/10 bg-white/5"><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="mt-3 flex gap-2 items-center">
              <span className="text-slate-500 text-sm">or code:</span>
              <span className="font-mono-data text-lg text-amber-400 tracking-widest" data-testid="invite-code">{t.invite_code}</span>
              <Button size="sm" variant="ghost" onClick={() => copy(t.invite_code)} className="text-slate-400"><Copy className="w-3 h-3" /></Button>
            </div>
          </Card>
          <StatBento icon={<Users />} label="Players" value={`${t.participants.length}/${t.total_players}`} />
          <StatBento icon={<Timer />} label="Timer" value={`${t.timer_minutes}m`} />
        </div>

        {/* Active round timer overlay */}
        {activeRound && remainingSec !== null && (
          <div className={`mt-8 rounded-2xl border p-8 ${isPaused ? 'border-sky-500/40 bg-gradient-to-br from-sky-500/5 to-transparent' : 'border-amber-500/40 bg-gradient-to-br from-amber-500/5 to-transparent'}`}>
            <div className="flex items-center justify-between flex-wrap gap-6">
              <div>
                <div className={`text-xs font-mono-data tracking-widest flex items-center gap-2 ${isPaused ? 'text-sky-400' : 'text-amber-400'}`}>
                  ROUND {activeRound.round_number} · {isPaused ? "PAUSED" : "IN PROGRESS"}
                  {isPaused && <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />}
                </div>
                <div className={`font-mono-data text-7xl md:text-9xl mt-2 ${remainingSec === 0 ? 'text-rose-400 flash-amber' : isPaused ? 'text-sky-200' : 'text-slate-50'}`} data-testid="round-timer">
                  {fmt(remainingSec)}
                </div>
              </div>
              <div className="flex flex-col gap-2 items-stretch min-w-[240px]">
                {remainingSec === 0 && !isPaused && (
                  <div className="text-rose-400 font-semibold">Time's up! Collect scores.</div>
                )}
                {isOrg && (
                  isPaused ? (
                    <Button size="lg" onClick={() => resumeRound(activeRound.round_number)} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold h-12 px-6" data-testid="resume-round">
                      <Play className="w-4 h-4 mr-2" /> Resume Timer
                    </Button>
                  ) : (
                    remainingSec > 0 && (
                      <Button size="lg" variant="outline" onClick={() => pauseRound(activeRound.round_number)} className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10 h-12 px-6" data-testid="pause-round">
                        <Pause className="w-4 h-4 mr-2" /> Pause Timer
                      </Button>
                    )
                  )
                )}
                {isOrg && (
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-14 px-8" onClick={() => openScoreDialog(activeRound.round_number - 1)} data-testid="enter-scores">
                    Enter Round Scores <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Organizer controls */}
        {isOrg && !isFinished && !activeRound && (
          <div className="mt-8 rounded-2xl border border-white/10 bg-slate-900/40 p-6">
            <Section label="Organizer controls" />
            <div className="mt-4 flex flex-wrap gap-3">
              {readyRound ? (
                <Button size="lg" onClick={() => startRound(readyRound.round_number)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-6" data-testid="start-round">
                  <Play className="w-4 h-4 mr-2" /> START Round {readyRound.round_number}
                </Button>
              ) : nextRoundIdx >= 0 ? (
                <Button size="lg" onClick={() => setupRound(nextRoundIdx + 1)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-6" data-testid="setup-round">
                  <Users className="w-4 h-4 mr-2" /> Set Tournament for Round {nextRoundIdx + 1}
                </Button>
              ) : null}
              {canStartChampionship && (
                <Button size="lg" variant="outline" onClick={() => setupRound(championshipIdx + 1)} className="border-fuchsia-500/50 bg-fuchsia-500/5 text-fuchsia-200 hover:bg-fuchsia-500/10 h-12 px-6" data-testid="start-championship">
                  <Crown className="w-4 h-4 mr-2" /> Seat Championship Round
                </Button>
              )}
              {championshipRnd?.status === "locked" && t.status !== "finished" && (
                <Button size="lg" onClick={finishTournament} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-12 px-6" data-testid="finish-tournament">
                  <Flag className="w-4 h-4 mr-2" /> Finish Tournament
                </Button>
              )}
              {/* Allow finishing after all regular rounds if organizer prefers */}
              {!championshipRnd?.status && t.rounds.every(r => r.status === "locked" || r.is_championship) && t.status !== "finished" && (
                <Button size="lg" onClick={finishTournament} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-12 px-6" data-testid="finish-tournament-alt">
                  Finish Tournament
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Cancel Tournament (always available to organizer while not finished) */}
        {isOrg && !isFinished && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={cancelTournament}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-rose-500/30 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10 text-sm font-mono-data tracking-widest uppercase transition-colors"
              data-testid="cancel-tournament"
            >
              <X className="w-4 h-4" /> Cancel Tournament
            </button>
          </div>
        )}

        {/* Podium when finished */}
        {isFinished && t.standings && t.standings.length >= 1 && (
          <Podium standings={t.standings} tournamentType={t.tournament_type} />
        )}

        {/* Ready round: show tables */}
        {(readyRound || activeRound) && (
          <RoundTables round={readyRound || activeRound} tournament={t} isOrganizer={isOrg} onReshuffle={reshuffleRound} />
        )}

        {/* Rounds progress */}
        <div className="mt-12">
          <h2 className="text-2xl text-slate-50">Round progress</h2>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {(() => {
              const lockedIdxs = t.rounds.map((r, i) => (r.status === "locked" ? i : -1)).filter(i => i >= 0);
              const latestLockedIdx = lockedIdxs.length ? Math.max(...lockedIdxs) : -1;
              const anyLater = latestLockedIdx >= 0 && t.rounds.slice(latestLockedIdx + 1).some(r => ["ready","active","locked"].includes(r.status));
              return t.rounds.map((rnd, idx) => (
                <RoundCard
                  key={idx}
                  round={rnd}
                  tournament={t}
                  isOrganizer={isOrg}
                  isLatestLocked={idx === latestLockedIdx && !anyLater && !isFinished}
                  onEdit={() => openScoreDialog(idx)}
                />
              ));
            })()}
          </div>
        </div>

        {/* Live standings */}
        <div className="mt-12">
          <h2 className="text-2xl text-slate-50">Standings</h2>
          <Standings standings={t.standings} tournamentType={t.tournament_type} />
        </div>

        {/* Participants */}
        <div className="mt-12">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-2xl text-slate-50">Participants <span className="text-slate-500 font-mono-data text-sm">({t.participants.length}/{t.total_players})</span></h2>
            {isOrg && t.status === "open" && (
              <AddGuestForm tid={tid} onAdded={load} disabled={t.participants.length >= t.total_players} />
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {t.participants.map(p => (
              <div key={p.user_id} className="group rounded-lg border border-white/10 bg-slate-900/50 px-4 py-3 flex items-start justify-between gap-2" data-testid={`participant-${p.user_id}`}>
                <div className="min-w-0">
                  <div className="text-slate-100 text-sm flex items-center gap-2">
                    <span className="truncate">{p.name}</span>
                    {p.is_guest && <span className="text-[9px] font-mono-data tracking-widest px-1.5 py-0.5 rounded border border-sky-500/40 bg-sky-500/10 text-sky-300 uppercase">Walk-in</span>}
                  </div>
                  <div className="text-slate-500 text-xs mt-0.5 truncate">{p.email || (p.is_guest ? "no account" : "—")}</div>
                </div>
                {isOrg && t.status === "open" && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Remove ${p.name}?`)) return;
                      try {
                        await api.delete(`/tournaments/${tid}/participants/${p.user_id}`);
                        toast.success("Player removed");
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed to remove");
                      }
                    }}
                    className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 transition-opacity text-xs"
                    data-testid={`remove-participant-${p.user_id}`}
                    title="Remove player"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Score entry dialog */}
      <Dialog open={scoreDialogOpen} onOpenChange={setScoreDialogOpen}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-100">
              {pendingRoundIdx !== null && t.rounds[pendingRoundIdx].status === "locked" ? "Edit" : "Enter"}
              {" "}
              {pendingRoundIdx !== null && t.rounds[pendingRoundIdx].is_championship ? "final match" : `round ${pendingRoundIdx !== null ? t.rounds[pendingRoundIdx].round_number : ""} scores`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-400 text-sm">
            {t.tournament_type === "bracket"
              ? "Pick the winner of each match. The winner advances to the next round."
              : (pendingRoundIdx !== null && t.rounds[pendingRoundIdx].status === "locked"
                  ? "Fix a typo? Update each player's victory points below and save."
                  : "Enter each player's raw victory points. The app calculates their share.")}
          </p>
          <div className="space-y-4 max-h-96 overflow-y-auto mt-2">
            {t.tournament_type === "bracket" && pendingRoundIdx !== null ? (
              t.rounds[pendingRoundIdx].tables.map(tbl => {
                const [a, b] = tbl.player_ids;
                const winnerUid = Number(scoresInput[a] || 0) > Number(scoresInput[b] || 0) ? a
                  : Number(scoresInput[b] || 0) > Number(scoresInput[a] || 0) ? b : null;
                const pick = (uid) => setScoresInput(s => ({ ...s, [a]: uid === a ? "1" : "0", [b]: uid === b ? "1" : "0" }));
                return (
                  <div key={tbl.table_number} className="rounded-xl border border-white/10 bg-slate-950/60 p-4">
                    <div className="text-[10px] font-mono-data tracking-widest text-emerald-400/80 mb-3">TABLE {tbl.table_number} · PICK WINNER</div>
                    {[a, b].map(uid => (
                      <label key={uid} className={`flex items-center gap-3 py-2 px-3 rounded-lg cursor-pointer transition-colors ${winnerUid === uid ? 'bg-amber-500/10 border border-amber-500/40' : 'hover:bg-white/5 border border-transparent'}`} data-testid={`bracket-winner-option-${uid}`}>
                        <input
                          type="radio"
                          name={`match-${tbl.table_number}`}
                          checked={winnerUid === uid}
                          onChange={() => pick(uid)}
                          className="accent-amber-400 w-4 h-4"
                          data-testid={`bracket-winner-radio-${uid}`}
                        />
                        <span className={`flex-1 text-sm ${winnerUid === uid ? 'text-amber-200 font-medium' : 'text-slate-200'}`}>{nameOf(uid)}</span>
                        {winnerUid === uid && <span className="text-[10px] font-mono-data tracking-widest text-amber-400">WINNER</span>}
                      </label>
                    ))}
                  </div>
                );
              })
            ) : (
              pendingRoundIdx !== null && t.rounds[pendingRoundIdx].tables.flatMap(x => x.player_ids).map(uid => (
                <div key={uid} className="flex items-center gap-3">
                  <div className="flex-1 text-slate-200 text-sm">{nameOf(uid)}</div>
                  <Input type="number" step="0.01" placeholder="0" value={scoresInput[uid] || ""} onChange={(e) => setScoresInput(s => ({ ...s, [uid]: e.target.value }))} className="w-32 bg-slate-950 border-white/10 font-mono-data" data-testid={`score-input-${uid}`} />
                  <span className="text-slate-500 text-xs">VP</span>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setScoreDialogOpen(false)} className="text-slate-400">Cancel</Button>
            <Button onClick={submitScores} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="submit-scores">
              {pendingRoundIdx !== null && t.rounds[pendingRoundIdx].status === "locked" ? "Save Changes" : "Lock in Round"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function playAlarm() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const t0 = ctx.currentTime;
    [0, 0.35, 0.7].forEach(delay => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.frequency.setValueAtTime(880, t0 + delay);
      g.gain.setValueAtTime(0, t0 + delay);
      g.gain.linearRampToValueAtTime(0.25, t0 + delay + 0.01);
      g.gain.linearRampToValueAtTime(0, t0 + delay + 0.28);
      o.connect(g); g.connect(ctx.destination);
      o.start(t0 + delay); o.stop(t0 + delay + 0.3);
    });
  } catch {}
}

function computeStandings(t) {
  return t.standings || [];
}

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-slate-900/40 p-6 ${className}`}>{children}</div>
);
const Section = ({ label }) => (
  <div className="text-xs text-slate-500 font-mono-data tracking-widest">{label.toUpperCase()}</div>
);

const StatBento = ({ icon, label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6 flex items-center justify-between">
    <div>
      <div className="text-xs text-slate-500 font-mono-data tracking-widest">{label.toUpperCase()}</div>
      <div className="text-3xl font-bold text-slate-50 font-mono-data mt-1">{value}</div>
    </div>
    <div className="w-10 h-10 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center">{icon}</div>
  </div>
);

function StatusPill({ status }) {
  const map = {
    open: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    in_progress: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    championship: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300",
    finished: "border-slate-500/30 bg-slate-500/10 text-slate-300",
    archived: "border-slate-500/30 bg-slate-500/10 text-slate-400",
  };
  return <span className={`text-[10px] font-mono-data tracking-widest px-2 py-1 rounded-md border uppercase ${map[status] || map.open}`}>{status?.replace("_", " ")}</span>;
}

function RoundTables({ round, tournament, isOrganizer, onReshuffle }) {
  const nameOf = (uid) => tournament.participants.find(p => p.user_id === uid)?.name || uid;
  const canReshuffle = isOrganizer && round.status === "ready";
  return (
    <div className="mt-10">
      <div className="flex items-baseline gap-4 flex-wrap">
        <h2 className="text-2xl text-slate-50">{round.is_championship || (tournament.tournament_type === "bracket" && round.round_number === tournament.num_rounds) ? "Championship / Final" : `Round ${round.round_number} Seating`}</h2>
        <span className="text-slate-500 font-mono-data text-xs uppercase tracking-widest">{round.status}</span>
        {canReshuffle && (
          <button
            onClick={() => onReshuffle(round.round_number)}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-slate-200 text-xs font-mono-data tracking-widest uppercase transition-colors"
            data-testid="reshuffle-round"
            title="Reroll table assignments"
          >
            <span>↻</span> Reshuffle Seats
          </button>
        )}
      </div>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {round.tables.map(tbl => (
          <div key={tbl.table_number} className="rounded-xl border border-white/10 bg-slate-900/60 p-6" data-testid={`table-${tbl.table_number}`}>
            <div className="text-xs text-emerald-400 font-mono-data tracking-widest">TABLE {tbl.table_number}</div>
            <ul className="mt-3 space-y-2">
              {tbl.player_ids.map(uid => (
                <li key={uid} className="text-slate-200 text-sm border-b border-white/5 pb-2 last:border-0">{nameOf(uid)}</li>
              ))}
            </ul>
          </div>
        ))}
        {round.byes.length > 0 && (
          <div className={`rounded-xl border p-6 ${tournament.tournament_type === "bracket" ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`}>
            <div className={`text-xs font-mono-data tracking-widest ${tournament.tournament_type === "bracket" ? "text-emerald-400" : "text-rose-400"}`}>
              {tournament.tournament_type === "bracket" ? "ADVANCED — BYE" : "BYE — SIT OUT"}
            </div>
            <ul className="mt-3 space-y-2">
              {round.byes.map(uid => (
                <li key={uid} className={`text-sm border-b border-white/5 pb-2 last:border-0 ${tournament.tournament_type === "bracket" ? "text-emerald-200" : "text-rose-200"}`}>{nameOf(uid)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function RoundCard({ round, tournament, isOrganizer, isLatestLocked, onEdit }) {
  const nameOf = (uid) => tournament.participants.find(p => p.user_id === uid)?.name || uid;
  const label = round.is_championship ? "CHAMPIONSHIP" : `ROUND ${round.round_number}`;
  const statusColor = {
    pending: "text-slate-500",
    ready: "text-emerald-400",
    active: "text-amber-400 animate-pulse",
    scored: "text-amber-400",
    locked: "text-slate-400",
  }[round.status];
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono-data tracking-widest text-slate-400">{label}</div>
        <div className={`text-xs font-mono-data uppercase ${statusColor}`}>{round.status}</div>
      </div>
      {round.status === "locked" && (
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">Round VP pool: <span className="font-mono-data text-slate-200">{round.total_points}</span></div>
            {isOrganizer && isLatestLocked && (
              <button
                onClick={onEdit}
                className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 font-mono-data tracking-widest uppercase"
                data-testid={`edit-round-${round.round_number}`}
              >
                Edit Scores →
              </button>
            )}
          </div>
          {(() => {
            // Table-based share: each player's points / their table's total
            const scoresMap = Object.fromEntries(round.scores.map(s => [s.user_id, s.points]));
            return round.tables.map(tbl => {
              const tableTotal = tbl.player_ids.reduce((a, uid) => a + (scoresMap[uid] || 0), 0);
              return (
                <div key={tbl.table_number} className="mt-3">
                  <div className="text-[10px] font-mono-data tracking-widest text-emerald-400/80">TABLE {tbl.table_number} · {tableTotal} VP</div>
                  <div className="mt-1 space-y-1 font-mono-data text-xs">
                    {tbl.player_ids
                      .slice()
                      .sort((a, b) => (scoresMap[b] || 0) - (scoresMap[a] || 0))
                      .map(uid => {
                        const pts = scoresMap[uid] || 0;
                        const share = tableTotal ? (pts / tableTotal) * 100 : 0;
                        return (
                          <div key={uid} className="flex justify-between">
                            <span className="text-slate-300">{nameOf(uid)}</span>
                            <span className="text-slate-500">{pts} · {share.toFixed(1)}%</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}

function Standings({ standings, tournamentType }) {
  if (!standings || standings.length === 0) return <div className="mt-4 text-slate-500 text-sm">No scores yet.</div>;
  const hasChamp = standings.some(s => s.championship_score !== undefined);
  const label = scoreLabel(tournamentType);
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-slate-900/40 overflow-hidden">
      <div className="grid grid-cols-12 px-6 py-3 text-xs font-mono-data tracking-widest text-slate-500 border-b border-white/5">
        <div className="col-span-1">#</div>
        <div className="col-span-4">PLAYER</div>
        {hasChamp && <div className="col-span-2 text-right text-amber-400">CHAMP</div>}
        <div className={`${hasChamp ? 'col-span-2' : 'col-span-3'} text-right`}>{label}</div>
        <div className="col-span-2 text-right">BEST ROUND</div>
        <div className="col-span-1 text-right">RDS</div>
      </div>
      {standings.map((s, i) => (
        <div key={s.user_id} className={`grid grid-cols-12 px-6 py-3 items-center ${i === 0 ? 'bg-amber-500/5' : ''}`} data-testid={`standings-row-${i}`}>
          <div className={`col-span-1 font-mono-data ${i < 3 ? 'text-amber-400' : 'text-slate-500'}`}>{i + 1}</div>
          <div className="col-span-4 text-slate-100 flex items-center gap-2">
            <span>{s.name}</span>
            {s.championship_score !== undefined && (
              <span className="text-[9px] font-mono-data tracking-widest px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 uppercase">Champ</span>
            )}
          </div>
          {hasChamp && (
            <div className="col-span-2 text-right font-mono-data text-amber-300">
              {s.championship_score !== undefined ? formatScore(s.championship_score, tournamentType) : '—'}
            </div>
          )}
          <div className={`${hasChamp ? 'col-span-2' : 'col-span-3'} text-right font-mono-data text-slate-100`}>{formatScore(s.total_score, tournamentType)}</div>
          <div className="col-span-2 text-right font-mono-data text-slate-400">{formatScore(s.best_round, tournamentType)}</div>
          <div className="col-span-1 text-right font-mono-data text-slate-500">{s.rounds_played}</div>
        </div>
      ))}
    </div>
  );
}

function Podium({ standings, tournamentType }) {
  const top3 = standings.slice(0, 3);
  const order = [1, 0, 2]; // 2nd, 1st, 3rd visually
  const heights = ["h-40", "h-52", "h-32"];
  const colors = ["bg-slate-300", "bg-amber-400", "bg-orange-700"];
  return (
    <div className="mt-10 rounded-2xl border border-amber-500/30 bg-gradient-to-b from-amber-500/5 to-transparent p-8">
      <div className="text-xs font-mono-data tracking-widest text-amber-400">CHAMPIONSHIP RESULTS</div>
      <h2 className="mt-2 text-3xl md:text-4xl text-slate-50 flex items-center gap-3"><Trophy className="w-8 h-8 text-amber-400" /> Podium</h2>
      <div className="mt-10 grid grid-cols-3 gap-4 items-end max-w-3xl">
        {order.map((rankIdx, i) => {
          const s = top3[rankIdx];
          if (!s) return <div key={i} />;
          const rank = rankIdx + 1;
          return (
            <div key={s.user_id} className="text-center">
              <div className="text-slate-100 mb-3">
                <div className="text-xs font-mono-data text-slate-500">#{rank}</div>
                <div className="text-lg mt-1">{s.name}</div>
                {s.championship_score !== undefined ? (
                  <>
                    <div className="font-mono-data text-amber-400 text-sm">{formatScore(s.championship_score, tournamentType)}</div>
                    <div className="text-[10px] font-mono-data text-slate-500 tracking-widest uppercase">championship</div>
                  </>
                ) : (
                  <div className="font-mono-data text-amber-400 text-sm">{formatScore(s.total_score, tournamentType)}</div>
                )}
              </div>
              <div className={`${heights[i]} ${colors[i]} rounded-t-lg flex items-start justify-center pt-3`}>
                <span className="text-slate-900 font-bold text-4xl font-mono-data">{rank}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function AddGuestForm({ tid, onAdded, disabled }) {
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await api.post(`/tournaments/${tid}/add-guest`, { name: n });
      toast.success(`${n} added as a walk-in player`);
      setName("");
      onAdded && onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex gap-2 items-center">
      <Input
        placeholder="Walk-in player name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bg-slate-950 border-white/10 w-56"
        data-testid="add-guest-name"
        disabled={disabled || busy}
      />
      <Button type="submit" disabled={disabled || busy || !name.trim()} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold" data-testid="add-guest-submit">
        {busy ? "…" : "Add"}
      </Button>
    </form>
  );
}
